// tslint:disable:max-line-length
import { HttpClientModule, HTTP_INTERCEPTORS } from "@angular/common/http";
import { ErrorHandler, NgModule } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import {
  NgbAccordionModule,
  NgbAlertModule,
  NgbDatepickerModule,
  NgbDropdownModule,
  NgbModalModule,
  NgbNavModule,
  NgbPaginationModule,
  NgbPopoverModule,
  NgbProgressbarModule,
  NgbTooltipModule,
  NgbTypeaheadModule,
} from "@ng-bootstrap/ng-bootstrap";
import { NgSelectModule } from "@ng-select/ng-select";
import { UIRouterModule } from "@uirouter/angular";
import { AccountOverviewComponent } from "./account-overview/account-overview.component";
import { AccountProjectsComponent } from "./account-overview/account-projects.component";
import { AccountCardsComponent } from "./account-overview/cards/cards.component";
import { AccountDeployCardsComponent } from "./account-overview/cards/deploy-cards.component";
import { AccountPerformanceCardsComponent } from "./account-overview/cards/performance-cards.component";
import { AdvancedSearchboxComponent } from "./advanced-search/advanced-search.component";
import { AutoSizeInputDirective } from "./advanced-search/auto-size-input.directive";
import { AirbrakeErrorHandler } from "./airbrake/airbrake-error-handler";
import { AirbrakeService } from "./airbrake/airbrake.service";
import { AccountApmCapReachedComponent } from "./alerts/account-apm-cap-reached/account-apm-cap-reached.component";
import { AccountDeactivationAlertComponent } from "./alerts/account-deactivation/account-deactivation.component";
import { AccountErrorCapReachedComponent } from "./alerts/account-error-cap-reached/account-error-cap-reached.component";
import { AlertsCommonComponent } from "./alerts/common/common.component";
import { DeprecatedHerokuEmailWarningAlertComponent } from "./alerts/deprecated-heroku-email-warning/deprecated-heroku-email-warning.component";
import { CustomAlertComponent } from "./alerts/custom-alert/custom-alert.component";
import { InvoicedPaymentFailedAlertComponent } from "./alerts/invoiced-payment-failed/invoiced-payment-failed.component";
import { ErrorQuotaUsedAlertComponent } from "./alerts/error-quota-used/error-quota-used.component";
import { ErrorQuotaWarningAlertComponent } from "./alerts/error-quota-warning/error-quota-warning.component";
import { OutdatedNotifierWarningAlertComponent } from "./alerts/outdated-notifier-warning/outdated-notifier-warning.component";
import { RateLimitAlertComponent } from "./alerts/rate-limit/rate-limit.component";
import { RestrictedWarningAlertComponent } from "./alerts/restricted-warning/restricted-warning.component";
import { UnauthorizedAlertComponent } from "./alerts/unauthorized/unauthorized.component";
import { UndoAlertComponent } from "./alerts/undo/undo.component";
import { ApdexTooltipTextComponent } from "./apdex-tooltip-text/apdex-tooltip-text.component";
import { AppComponent } from "./app.component";
import { AuthService } from "./auth/auth.service";
import { BarChartComponent } from "./chart/bar-chart.component";
import { ChartHelperService } from "./chart/chart-helper.service";
import { LineChartComponent } from "./chart/line-chart.component";
import { StackedBarChartComponent } from "./chart/stacked-bar-chart.component";
import { TicksAgoService } from "./chart/ticks-ago.service";
import { CleanFilePathService } from "./clean-file/clean-file-path.service";
import { ClipboardService } from "./clipboard/clipboard.service";
import { CommentListComponent } from "./comment-list/comment-list.component";
import { CommitsUrlService } from "./commits-url/commits-url.service";
import { CSSColorService } from "./css-color/css-color.service";
import { DefaultChartResolutionService } from "./default-chart-resolution/default-chart-resolution.service";
import { DeployBarchartComponent } from "./deploy-barchart/deploy-barchart.component";
import { DeployViewComponent } from "./deploy-view/deploy-view.component";
import { DeploysWithErrorsComponent } from "./deploys-with-errors/deploys-with-errors.component";
import { DiffStatBlocksComponent } from "./diff-stats/diff-stat-blocks.component";
import { DurationFormatComponent } from "./duration-format/duration-format.component";
import { DurationFormatService } from "./duration-format/duration-format.service";
import { EnvColorService } from "./env-color/env-color.service";
import { EnvLabelComponent } from "./env-label/env-label.component";
import { ErrorMessageWithoutTypePipe } from "./error-message-without-type/error-message-without-type.pipe";
import { ErrorMessageWithoutTypeService } from "./error-message-without-type/error-message-without-type.service";
import { ErrorRateListService } from "./error-rate-list/error-rate-list.service";
import { ErrorsComponent } from "./errors/errors.component";
import { EventService } from "./event-service/event.service";
import { ExternalLinkComponent } from "./external-link/external-link.component";
import { FeatureFlagService } from "./feature-flag/feature-flag.service";
import { FormatDeployLabelService } from "./format-deploy-label/format-deploy-label.service";
import { GetStartedModalComponent } from "./get-started-modal/get-started-modal.component";
import { GetStartedModalService } from "./get-started-modal/get-started-modal.service";
import { GravatarComponent } from "./gravatar/gravatar.component";
import { FilterParamsService } from "./group-filter/filter-params/filter-params.service";
import { GroupFilterComponent } from "./group-filter/group-filter.component";
import { SearchDataService } from "./group-filter/search-data/search-data.service";
import { GroupListComponent } from "./group-list/group-list.component";
import { GroupPollerComponent } from "./group-list/group-poller/group-poller.component";
import { ScrollMeDirective } from "./group-list/scroll-me.directive";
import { HubspotChatService } from "./hubspot-chat/hubspot-chat.service";
import { IgnoreNullsSortService } from "./ignore-nulls-sort/ignore-nulls-sort.service";
import { AssemblaIntegrationComponent } from "./integration/assembla/assembla.component";
import { BitbucketIntegrationComponent } from "./integration/bitbucket/bitbucket.component";
import { GithubIntegrationComponent } from "./integration/github/github.component";
import { GitlabIntegrationComponent } from "./integration/gitlab/gitlab.component";
import { JiraIntegrationComponent } from "./integration/jira/jira.component";
import { LighthouseIntegrationComponent } from "./integration/lighthouse/lighthouse.component";
import { PivotalIntegrationComponent } from "./integration/pivotal/pivotal.component";
import { TrelloIntegrationComponent } from "./integration/trello/trello.component";
import { IssueIndicatorsService } from "./issue-indicators/issue-indicators.service";
import { KeyboardService } from "./keyboard/keyboard.service";
import { MarkdownDirective } from "./markdown/markdown.directive";
import { MarkdownService } from "./markdown/markdown.service";
import { AccountService } from "./models/account.service";
import { ActivitiesV5Service } from "./models/activities-v5.service";
import { ActivitiesService } from "./models/activities.service";
import { AttributeService } from "./models/attribute.service";
import { ColorModeService } from "./models/color-mode.service";
import { CommentService } from "./models/comment.service";
import { DeployService } from "./models/deploy.service";
import { DiffStatAggregationsService } from "./models/diff-stat-aggregations.service";
import { DiffStatHotspotsService } from "./models/diff-stat-hotspots.service";
import { DiffStatsService } from "./models/diff-stats.service";
import { GroupAttrService } from "./models/group-attr.service";
import { GroupService } from "./models/group.service";
import { InstructionsService } from "./models/instructions.service";
import { AssemblaTicketService } from "./models/integrations/assembla-ticket.service";
import { BitbucketIssueService } from "./models/integrations/bitbucket-issue.service";
import { GithubIssueService } from "./models/integrations/github-issue.service";
import { GitlabIssueService } from "./models/integrations/gitlab-issue.service";
import { JiraIssueService } from "./models/integrations/jira-issue.service";
import { LighthouseTicketService } from "./models/integrations/lighthouse-ticket.service";
import { PivotalStoryService } from "./models/integrations/pivotal-story.service";
import { TrelloCardService } from "./models/integrations/trello-card.service";
import { MultiProjectRoutesStatsService } from "./models/multi-project-routes-stats.service";
import { NoticeService } from "./models/notice.service";
import { NotifierPropertiesService } from "./models/notifier-properties.service";
import { OnboardingTaskService } from "./models/onboarding-task.service";
import { PerformanceBreakdownService } from "./models/performance-breakdown.service";
import { ProjectAttrService } from "./models/project-attr.service";
import { ProjectDigestsService } from "./models/project-digests.service";
import { ProjectLanguageService } from "./models/project-language.service";
import { ProjectService } from "./models/project.service";
import { PullRequestService } from "./models/pull-request.service";
import { QueriesEnvironmentsService } from "./models/queries-environments.service";
import { QueriesService } from "./models/queries.service";
import { QueuesEnvironmentsService } from "./models/queues-environments.service";
import { QueuesStatsService } from "./models/queues-stats.service";
import { QueuesService } from "./models/queues.service";
import { ResponseTypeService } from "./models/response-type.service";
import { RoutesEnvironmentsService } from "./models/routes-environments.service";
import { RoutesStatsService } from "./models/routes-stats.service";
import { RoutesService } from "./models/routes.service";
import { SendDataService } from "./models/send-data.service";
import { StatsmanService } from "./models/statsman.service";
import { UserService } from "./models/user.service";
import { CustomAlertService } from "./models/custom-alert.service";
import { AttrGroupComponent } from "./notice-view/attr-list/attr-group/attr-group.component";
import { AttrKeyLastFieldPipe } from "./notice-view/attr-list/attr-group/attr-key-last-field/attr-key-last-field.pipe";
import { AttrValuePipe } from "./notice-view/attr-list/attr-group/attr-value/attr-value.pipe";
import { AttrListComponent } from "./notice-view/attr-list/attr-list.component";
import { AttrPreviewComponent } from "./notice-view/attr-list/attr-preview/attr-preview.component";
import { BacktraceComponent } from "./notice-view/backtrace/backtrace.component";
import { CountryComponent } from "./notice-view/country/country.component";
import { FileLineComponent } from "./notice-view/file-line/file-line.component";
import { BreadcrumbsComponent } from "./notice-view/notice-detail/breadcrumbs/breadcrumbs.component";
import { NoticeDetailComponent } from "./notice-view/notice-detail/notice-detail.component";
import { NoticeListComponent } from "./notice-view/notice-list/notice-list.component";
import { NoticeOverviewComponent } from "./notice-view/notice-overview/notice-overview.component";
import { NoticeViewComponent } from "./notice-view/notice-view.component";
import { UserAgentComponent } from "./notice-view/user-agent/user-agent.component";
import { NumberToHumanPipe } from "./number-to-human/number-to-human.pipe";
import { OccurrencesComponent } from "./occurrences/occurrences.component";
// Onboarding callouts.
import { AddApmCalloutComponent } from "./onboarding/items/add-apm-callout.component";
import { AddDeploysCalloutComponent } from "./onboarding/items/add-deploys-callout.component";
import { AddSlackCalloutComponent } from "./onboarding/items/add-slack-callout.component";
import { NameFirstProjectCalloutComponent } from "./onboarding/items/name-first-project-callout.component";
import { ShowAggregationsCalloutComponent } from "./onboarding/items/show-aggregations-callout.component";
import { ShowOnboardingChecklistCalloutComponent } from "./onboarding/items/show-onboarding-checklist-callout.component";
// End onboarding callouts.
import { OnboardingCalloutComponent } from "./onboarding/onboarding-callout.component";
import { OnboardingChecklistComponent } from "./onboarding/onboarding-checklist.component";
import { OnboardingProgressComponent } from "./onboarding/onboarding-progress.component";
import { OnboardingTaskStateService } from "./onboarding/onboarding-task-state.service";
import { OutdatedNotifierService } from "./outdated-notifier/outdated-notifier.service";
import { PageVisibilityService } from "./page-visibility/page-visibility.service";
import { PlanTierService } from "./plan-tier/plan-tier.service";
import { PluralizePipe } from "./pluralize/pluralize.pipe";
import { CommentActivityComponent } from "./project-activities/activity/comment-activity.component";
import { DeployActivityComponent } from "./project-activities/activity/deploy-activity.component";
import { GroupActivityComponent } from "./project-activities/activity/group-activity.component";
import { UserActivityComponent } from "./project-activities/activity/user-activity.component";
import { ProjectActivitiesComponent } from "./project-activities/project-activities.component";
import { AlertDurationPctPipe } from "./project-alerts/duration-pct/duration-pct.pipe";
import { AlertDurationPipe } from "./project-alerts/duration/duration.pipe";
import { AlertFilterPipe } from "./project-alerts/filter/filter.pipe";
import { AlertMetricPipe } from "./project-alerts/metric/metric.pipe";
import { ProjectAlertsComponent } from "./project-alerts/project-alerts.component";
import { AlertSeverityPipe } from "./project-alerts/severity/severity.pipe";
import { DeployCardsComponent } from "./project-deploys/deploy-column/deploy-cards.component";
import { DeployChartComponent } from "./project-deploys/deploy-column/deploy-chart.component";
import { DeployColumnComponent } from "./project-deploys/deploy-column/deploy-column.component";
import { DeployHelperService } from "./project-deploys/deploy-helper.service";
import { DeployListComponent } from "./project-deploys/deploy-list.component";
import { ProjectDeploysComponent } from "./project-deploys/project-deploys.component";
import { ProjectCardsComponent } from "./project-detail/cards/cards.component";
import { AttrHotspotsComponent } from "./project-detail/hotspots/attr-hotspots.component";
import { DiffStatHotspotsComponent } from "./project-detail/hotspots/diff-stat-hotspots.component";
import { HotspotDotColorService } from "./project-detail/hotspots/dot-colors.service";
import { HotspotsComponent } from "./project-detail/hotspots/hotspots.component";
import { InstructionsModalComponent } from "./project-detail/instructions-modal/instructions-modal.component";
import { InstructionsModalService } from "./project-detail/instructions-modal/instructions-modal.service";
import { CopyButtonWithTooltipComponent } from "./project-detail/instructions/copy-button-with-tooltip.component";
import { InstructionsComponent } from "./project-detail/instructions/instructions.component";
import { ProjectDetailComponent } from "./project-detail/project-detail.component";
import { ProjectLanguageIconComponent } from "./project-language-icon/project-language-icon.component";
import { ProjectNavComponent } from "./project-nav/project-nav.component";
import { ApdexCardComponent } from "./project-performance/apdex-card/apdex-card.component";
import { EnvironmentSwitchComponent } from "./project-performance/environment-switch/environment-switch.component";
import { ErrorCardsComponent } from "./project-performance/error-cards/error-cards.component";
import { ProjectPerformanceComponent } from "./project-performance/project-performance.component";
import { QueryListRouteComponent } from "./project-performance/query-list/query-list-route/query-list-route.component";
import { QueryListComponent } from "./project-performance/query-list/query-list.component";
import { QueueListComponent } from "./project-performance/queue-list/queue-list.component";
import { RequestMethodSwitchComponent } from "./project-performance/request-method-switch/request-method-switch.component";
import { ResponseCodeSwitchComponent } from "./project-performance/response-code-switch/response-code-switch.component";
import { ResponseCodesBarComponent } from "./project-performance/response-codes-bar/response-codes-bar.component";
import { RouteCardsComponent } from "./project-performance/route-cards/route-cards.component";
import { RouteChartsComponent } from "./project-performance/route-charts/route-charts.component";
import { RouteMethodComponent } from "./project-performance/route-method/route-method.component";
import { RouteNameComponent } from "./project-performance/route-name/route-name.component";
import { RouteTableComponent } from "./project-performance/route-table/route-table.component";
import { PerformanceBreakdownComponent } from "./project-performance/route-view/performance-breakdown/performance-breakdown.component";
import { HighlightSqlPipe } from "./project-performance/route-view/query-table/highlight-sql/highlight-sql.pipe";
import { QueryTableComponent } from "./project-performance/route-view/query-table/query-table.component";
import { RouteViewComponent } from "./project-performance/route-view/route-view.component";
import { ProjectPickerComponent } from "./project-picker/project-picker.component";
import { DigestStatsComponent } from "./project-trends/digest-stats/digest-stats.component";
import { DigestStatsTrendComponent } from "./project-trends/digest-stats/trend/trend.component";
import { ProjectTrendsComponent } from "./project-trends/project-trends.component";
import { RateFormatPipe } from "./rate-format/rate-format.pipe";
import { RememberService } from "./remember/remember.service";
import { RepositoryUrlService } from "./repository-url/repository-url.service";
import { RevisionCompareUrlService } from "./revision-compare-url/revision-compare-url.service";
import { RootUrlHandlerService } from "./root-url-handler/root-url-handler.service";
import { RoundTimeService } from "./round-time/round-time.service";
import { routerConfigFn } from "./router.config";
import { SendDataModalComponent } from "./send-data-modal/send-data-modal.component";
import { SendDataModalService } from "./send-data-modal/send-data-modal.service";
import { SetFocusOnDirective } from "./set-focus/set-focus-on.directive";
import { SetFocusService } from "./set-focus/set-focus.service";
import { ShortcutsModalComponent } from "./shortcuts-modal/shortcuts-modal.component";
import { ShortcutsModalService } from "./shortcuts-modal/shortcuts-modal.service";
import { SpinnerInlineComponent } from "./spinner/spinner-inline.component";
import { SpinnerComponent } from "./spinner/spinner.component";
import { APP_STATES } from "./states/states";
import { StatsNormalizerService } from "./stats-normalizer/stats-normalizer.service";
import { StdAttrsService } from "./std-attrs/std-attrs.service";
import { StringToColorService } from "./string-to-color/string-to-color.service";
import { TimeAgoComponent } from "./time-ago/time-ago.component";
import { TimeDurAgoService } from "./time-dur-ago/time-dur-ago.service";
import { TimeDurSwitchComponent } from "./time-dur-switch/time-dur-switch.component";
import { TimeFormatPipe } from "./time-format/time-format.pipe";
import { TimeFormatService } from "./time-format/time-format.service";
import { TimestampComponent } from "./timestamp/timestamp.component";
import { TitleizePipe } from "./title-case/titleize.pipe";
import { TrackerService } from "./tracker/tracker.service";
import { TrendComponent } from "./trend/trend.component";
import { TruncatePipe } from "./truncate/truncate.pipe";
import { UpgradeModalComponent } from "./upgrade-modal/upgrade-modal.component";
import { UpgradeModalService } from "./upgrade-modal/upgrade-modal.service";
import { UpperCaseSplitPipe } from "./upper-case-split/upper-case-split.pipe";
import { AccountViewComponent } from "./workbench/account-view.component";
import { AlertsViewComponent } from "./workbench/alerts-view.component";
import { DeploysViewComponent } from "./workbench/deploys-view.component";
import { PerformanceViewComponent } from "./workbench/performance-view.component";
import { TrendsViewComponent } from "./workbench/trends-view.component";
import { WorkbenchComponent } from "./workbench/workbench.component";
import { ApplyPurePipe } from "./advanced-search/pure.pipe";

@NgModule({
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    UIRouterModule.forRoot({
      states: APP_STATES,
      config: routerConfigFn,
    }),
    FormsModule,
    NgbTooltipModule,
    NgbDropdownModule,
    NgbAlertModule,
    NgbAccordionModule,
    NgSelectModule,
    NgbProgressbarModule,
    NgbPaginationModule,
    NgbModalModule,
    NgbTypeaheadModule,
    NgbDatepickerModule,
    NgbNavModule,
    NgbPopoverModule,
  ],
  providers: [
    // Angular
    ClipboardService,
    PageVisibilityService,
    UpgradeModalService,
    SetFocusService,
    SearchDataService,
    HotspotDotColorService,
    ErrorRateListService,
    TicksAgoService,
    ChartHelperService,
    CommitsUrlService,
    CSSColorService,
    EnvColorService,
    ErrorMessageWithoutTypeService,
    ShortcutsModalService,
    StdAttrsService,
    FormatDeployLabelService,
    KeyboardService,
    TimeFormatService,
    StringToColorService,
    DeployHelperService,
    RepositoryUrlService,
    RoundTimeService,
    TimeDurAgoService,
    RevisionCompareUrlService,
    InstructionsService,
    SendDataService,
    NotifierPropertiesService,
    ProjectLanguageService,
    QueriesService,
    PullRequestService,
    RoutesEnvironmentsService,
    QueriesEnvironmentsService,
    RoutesService,
    ActivitiesV5Service,
    ActivitiesService,
    CommentService,
    DiffStatHotspotsService,
    DiffStatAggregationsService,
    GroupAttrService,
    ResponseTypeService,
    DiffStatsService,
    PerformanceBreakdownService,
    RoutesStatsService,
    ProjectAttrService,
    PlanTierService,
    AccountService,
    StatsmanService,
    IssueIndicatorsService,
    GithubIssueService,
    PivotalStoryService,
    JiraIssueService,
    LighthouseTicketService,
    AssemblaTicketService,
    BitbucketIssueService,
    TrelloCardService,
    GitlabIssueService,
    DeployService,
    HubspotChatService,
    FeatureFlagService,
    FilterParamsService,
    RootUrlHandlerService,
    UserService,
    ProjectService,
    NoticeService,
    AttributeService,
    GroupService,
    EventService,
    DefaultChartResolutionService,
    AuthService,
    RememberService,
    MarkdownService,
    AirbrakeService,
    OutdatedNotifierService,
    CustomAlertService,
    QueuesService,
    ProjectDigestsService,
    QueuesStatsService,
    QueuesEnvironmentsService,
    StatsNormalizerService,
    MultiProjectRoutesStatsService,
    CleanFilePathService,
    IgnoreNullsSortService,
    DurationFormatService,
    OnboardingTaskService,
    OnboardingTaskStateService,
    GetStartedModalService,
    InstructionsModalService,
    TrackerService,
    ColorModeService,
    SendDataModalService,
    { provide: HTTP_INTERCEPTORS, useClass: AuthService, multi: true },
    { provide: ErrorHandler, useClass: AirbrakeErrorHandler },
  ],
  declarations: [
    // Components
    AppComponent,
    GravatarComponent,
    TimeDurSwitchComponent,
    LineChartComponent,
    StackedBarChartComponent,
    BarChartComponent,
    TimeAgoComponent,
    EnvLabelComponent,
    ErrorCardsComponent,
    TimestampComponent,
    TrendComponent,
    ProjectNavComponent,
    ApdexCardComponent,
    RouteMethodComponent,
    RouteNameComponent,
    ResponseCodesBarComponent,
    ProjectCardsComponent,
    RouteCardsComponent,
    RouteChartsComponent,
    DeployCardsComponent,
    FileLineComponent,
    ExternalLinkComponent,
    QueryTableComponent,
    RequestMethodSwitchComponent,
    ResponseCodeSwitchComponent,
    CountryComponent,
    EnvironmentSwitchComponent,
    UserAgentComponent,
    CommentActivityComponent,
    PerformanceBreakdownComponent,
    GroupActivityComponent,
    UserActivityComponent,
    DeployActivityComponent,
    RouteTableComponent,
    ProjectActivitiesComponent,
    DeployChartComponent,
    RouteViewComponent,
    ProjectPerformanceComponent,
    AssemblaIntegrationComponent,
    BitbucketIntegrationComponent,
    GithubIntegrationComponent,
    GitlabIntegrationComponent,
    BreadcrumbsComponent,
    DiffStatBlocksComponent,
    JiraIntegrationComponent,
    DeploysWithErrorsComponent,
    LighthouseIntegrationComponent,
    PivotalIntegrationComponent,
    TrelloIntegrationComponent,
    DeployColumnComponent,
    DiffStatHotspotsComponent,
    AttrHotspotsComponent,
    GroupPollerComponent,
    HotspotsComponent,
    AccountDeactivationAlertComponent,
    CustomAlertComponent,
    InvoicedPaymentFailedAlertComponent,
    ErrorQuotaUsedAlertComponent,
    ErrorQuotaWarningAlertComponent,
    OutdatedNotifierWarningAlertComponent,
    OccurrencesComponent,
    AccountCardsComponent,
    RateLimitAlertComponent,
    NoticeListComponent,
    BacktraceComponent,
    RestrictedWarningAlertComponent,
    DeprecatedHerokuEmailWarningAlertComponent,
    UnauthorizedAlertComponent,
    AlertsCommonComponent,
    NoticeDetailComponent,
    ProjectAlertsComponent,
    DeployViewComponent,
    UndoAlertComponent,
    AttrPreviewComponent,
    InstructionsComponent,
    CopyButtonWithTooltipComponent,
    InstructionsModalComponent,
    OnboardingProgressComponent,
    DeployBarchartComponent,
    ProjectDetailComponent,
    AttrGroupComponent,
    AttrListComponent,
    AccountProjectsComponent,
    AccountOverviewComponent,
    DeployListComponent,
    ProjectDeploysComponent,
    CommentListComponent,
    AddSlackCalloutComponent,
    NameFirstProjectCalloutComponent,
    ShowAggregationsCalloutComponent,
    AddApmCalloutComponent,
    AddDeploysCalloutComponent,
    ShowOnboardingChecklistCalloutComponent,
    OnboardingCalloutComponent,
    OnboardingChecklistComponent,
    UpgradeModalComponent,
    NoticeViewComponent,
    NoticeOverviewComponent,
    GroupListComponent,
    ProjectPickerComponent,
    AdvancedSearchboxComponent,
    GroupFilterComponent,
    ErrorsComponent,
    WorkbenchComponent,
    PerformanceViewComponent,
    AccountViewComponent,
    AlertsViewComponent,
    DeploysViewComponent,
    ShortcutsModalComponent,
    SpinnerComponent,
    SpinnerInlineComponent,
    QueryListComponent,
    QueueListComponent,
    ProjectLanguageIconComponent,
    QueryListRouteComponent,
    TrendsViewComponent,
    ProjectTrendsComponent,
    DigestStatsComponent,
    AccountPerformanceCardsComponent,
    AccountDeployCardsComponent,
    ApdexTooltipTextComponent,
    DigestStatsTrendComponent,
    AccountApmCapReachedComponent,
    AccountErrorCapReachedComponent,
    GetStartedModalComponent,
    SendDataModalComponent,
    // Pipes
    NumberToHumanPipe,
    DurationFormatComponent,
    RateFormatPipe,
    TimeFormatPipe,
    TruncatePipe,
    PluralizePipe,
    ErrorMessageWithoutTypePipe,
    TitleizePipe,
    AlertDurationPipe,
    AlertDurationPctPipe,
    AlertMetricPipe,
    AlertSeverityPipe,
    AlertFilterPipe,
    AttrValuePipe,
    AttrKeyLastFieldPipe,
    UpperCaseSplitPipe,
    HighlightSqlPipe,
    ApplyPurePipe,
    // Directives
    MarkdownDirective,
    SetFocusOnDirective,
    ScrollMeDirective,
    AutoSizeInputDirective,
  ],
  bootstrap: [AppComponent],
})
export class AppModule { }
